import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ActressCard from "@/components/feature/ActressCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import SortSelect from "@/components/base/SortSelect";
import CategoryFilters, { type CategoryFilterState } from "@/pages/category/components/CategoryFilters";
import useSEO from "@/hooks/useSEO";
import { actresses } from "@/mocks/actresses";

const PAGE_SIZE = 20;

const AGE_RANGES = [
  { value: "18-24", label: "18-24 岁", min: 18, max: 24 },
  { value: "25-29", label: "25-29 岁", min: 25, max: 29 },
  { value: "30-34", label: "30-34 岁", min: 30, max: 34 },
  { value: "35-39", label: "35 岁以上", min: 35, max: 99 },
];

const DEBUT_RANGES = [
  { value: "2011-2014", label: "2011-2014", min: 2011, max: 2014 },
  { value: "2015-2017", label: "2015-2017", min: 2015, max: 2017 },
  { value: "2018-2020", label: "2018-2020", min: 2018, max: 2020 },
  { value: "2021-2023", label: "2021-2023", min: 2021, max: 2023 },
];

const SORT_OPTIONS = [
  { value: "name", label: "姓名拼音 A-Z" },
  { value: "debut", label: "出道时间（新→旧）" },
  { value: "works", label: "作品数量（多→少）" },
];

const AGENCIES = Array.from(new Set(actresses.map((item) => item.agency)));

function inRange(value: number, min: number, max: number) {
  return value >= min && value <= max;
}

export default function CategoryPage() {
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState<CategoryFilterState>(() => {
    const age = searchParams.get("age");
    const debut = searchParams.get("debut");
    const status = searchParams.get("status");
    return {
      ages: AGE_RANGES.some((item) => item.value === age) ? [age as string] : [],
      debuts: DEBUT_RANGES.some((item) => item.value === debut) ? [debut as string] : [],
      statuses: status === "active" ? ["活跃"] : status === "paused" ? ["休业"] : [],
      agencies: [],
    };
  });
  const [sort, setSort] = useState(searchParams.get("sort") === "works" ? "works" : "name");
  const [page, setPage] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);

  const filtered = useMemo(() => {
    return actresses.filter((actress) => {
      if (filters.ages.length > 0) {
        const matched = filters.ages.some((value) => {
          const range = AGE_RANGES.find((item) => item.value === value);
          return range ? inRange(actress.age, range.min, range.max) : true;
        });
        if (!matched) return false;
      }
      if (filters.debuts.length > 0) {
        const matched = filters.debuts.some((value) => {
          const range = DEBUT_RANGES.find((item) => item.value === value);
          return range ? inRange(actress.debutYear, range.min, range.max) : true;
        });
        if (!matched) return false;
      }
      if (filters.statuses.length > 0 && !filters.statuses.includes(actress.status)) {
        return false;
      }
      if (filters.agencies.length > 0 && !filters.agencies.includes(actress.agency)) {
        return false;
      }
      return true;
    });
  }, [filters]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    if (sort === "debut") {
      list.sort((a, b) => b.debutYear - a.debutYear || a.nameRomaji.localeCompare(b.nameRomaji));
    } else if (sort === "works") {
      list.sort((a, b) => b.worksCount - a.worksCount || a.nameRomaji.localeCompare(b.nameRomaji));
    } else {
      list.sort((a, b) => a.nameRomaji.localeCompare(b.nameRomaji));
    }
    return list;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const ageOptions = AGE_RANGES.map((range) => ({
    value: range.value,
    label: range.label,
    count: actresses.filter((item) => inRange(item.age, range.min, range.max)).length,
  }));

  const debutOptions = DEBUT_RANGES.map((range) => ({
    value: range.value,
    label: range.label,
    count: actresses.filter((item) => inRange(item.debutYear, range.min, range.max)).length,
  }));

  const statusOptions = [
    {
      value: "活跃",
      label: "活跃中",
      count: actresses.filter((item) => item.status === "活跃").length,
    },
    {
      value: "休业",
      label: "休业中",
      count: actresses.filter((item) => item.status === "休业").length,
    },
  ];

  const agencyOptions = AGENCIES.map((agency) => ({
    value: agency,
    label: agency,
    count: actresses.filter((item) => item.agency === agency).length,
  }));

  const selectedCount =
    filters.ages.length + filters.debuts.length + filters.statuses.length + filters.agencies.length;

  const activeLabels = [
    ...filters.ages,
    ...filters.debuts,
    ...filters.statuses,
    ...filters.agencies,
  ];

  const titleText =
    activeLabels.length > 0
      ? `按${activeLabels.slice(0, 2).join("、")}筛选的日本女优分类`
      : "日本女优分类 - 按年龄、出道年份与活跃状态筛选";

  useSEO({
    title: `${titleText} - 日本女优大全`,
    description:
      activeLabels.length > 0
        ? `日本女优分类页：当前按${activeLabels.join("、")}筛选，共收录 ${sorted.length} 位女优档案，包含简介、所属事务所与代表作品，可继续组合筛选与排序。`
        : "日本女优分类页按年龄段、出道年份、活跃状态与所属事务所整理女优档案，支持多选筛选与姓名、出道时间、作品数量排序。",
    keywords: "日本女优分类,女优列表,女优年龄,女优出道年份,日本女优资料",
  });

  const toggleFilter = (key: keyof CategoryFilterState, value: string) => {
    setFilters((prev) => {
      const current = prev[key];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, [key]: next };
    });
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ ages: [], debuts: [], statuses: [], agencies: [] });
    setPage(1);
  };

  return (
    <SiteLayout>
      <Breadcrumbs items={[{ label: "女优分类" }]} />

      <div className="mb-6 border-b border-background-200 pb-5">
        <p className="font-label text-[11px] tracking-[0.2em] text-primary-600">ACTRESS CATEGORY</p>
        <h1 className="mt-1.5 font-heading text-[26px] font-black leading-tight text-foreground-950 md:text-[32px]">
          女优分类
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
          按年龄段、出道年份、活跃状态与所属事务所浏览档案。分类依据以公开资料整理，同一女优可能同时符合多个条件。点击卡片进入完整档案，查看简介、出演作品与相关新闻。
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[232px_1fr]">
        <div>
          <button
            type="button"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-expanded={mobileOpen}
            className="flex w-full cursor-pointer items-center justify-between rounded-md border border-background-300 bg-background-50 px-4 py-3 font-label text-sm text-foreground-800 lg:hidden"
          >
            <span className="flex items-center gap-2">
              <i className="ri-equalizer-line text-primary-600" aria-hidden="true"></i>
              筛选条件
              {selectedCount > 0 ? (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] text-background-50">
                  {selectedCount}
                </span>
              ) : null}
            </span>
            <i
              className={`${mobileOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-foreground-500`}
              aria-hidden="true"
            ></i>
          </button>

          <div className={`mt-3 ${mobileOpen ? "block" : "hidden"} lg:mt-0 lg:block`}>
            <div className="lg:sticky lg:top-20">
              <CategoryFilters
                state={filters}
                onToggle={toggleFilter}
                onReset={resetFilters}
                ageOptions={ageOptions}
                debutOptions={debutOptions}
                statusOptions={statusOptions}
                agencyOptions={agencyOptions}
              />
            </div>
          </div>
        </div>

        <div>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="font-label text-xs text-foreground-500">
              共 <span className="font-medium text-primary-700">{sorted.length}</span> 位女优
              {selectedCount > 0 ? ` · 已应用 ${selectedCount} 个条件` : ""}
            </p>
            <SortSelect value={sort} options={SORT_OPTIONS} onChange={(value) => {
              setSort(value);
              setPage(1);
            }} />
          </div>

          {activeLabels.length > 0 ? (
            <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md bg-background-50 px-3 py-2">
              <span className="font-label text-[11px] text-foreground-500">已选：</span>
              {activeLabels.map((label) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 font-label text-[11px] text-primary-700"
                >
                  {label}
                </span>
              ))}
              <button
                type="button"
                onClick={resetFilters}
                className="ml-1 cursor-pointer font-label text-[11px] text-foreground-500 underline underline-offset-2 hover:text-primary-700"
              >
                清空
              </button>
            </div>
          ) : null}

          {visible.length > 0 ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {visible.map((actress) => (
                  <ActressCard key={actress.id} actress={actress} />
                ))}
              </div>
              <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
              <p className="mt-4 text-center font-label text-[11px] text-foreground-400">
                每页 20 位 · 当前第 {currentPage} / {totalPages} 页
              </p>
            </>
          ) : (
            <EmptyState
              title="没有符合条件的女优"
              description="当前筛选组合下没有匹配的档案。可以尝试减少筛选条件，或直接重置查看全部女优。"
              onReset={resetFilters}
              actionLabel="返回首页"
              actionHref="/"
            />
          )}
        </div>
      </div>
    </SiteLayout>
  );
}